import { DelayedSkeleton } from '@/components/ui/delayed-skeleton';
import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityDetailsHeader } from '@/components/ui/entity-details-header';
import { PeriodDisplay } from '@/components/ui/period-display';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import type { TariffQuery } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { useTariffTypeLookup } from '@/hooks/useTariffTypeLookup';
import { FileText, History, Layers, Link2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TARIFF_QUERY } from './tariffs.gql.tsx';

import { TariffReferencesTab } from './tariff-references-tab';

type TariffDetailsData = NonNullable<TariffQuery['tariff']>;
type TariffStep = NonNullable<
	NonNullable<TariffDetailsData['tariffSteps']>[number]
>;

interface TariffDetailsProps {
	tariffId: string;
	activeTab?: string;
	onTabChange?: (tab: string) => void;
	hideTabsList?: boolean;
	// Header props for modal display
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

const TariffEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<Layers className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export function TariffDetails({
	tariffId,
	activeTab,
	onTabChange,
	hideTabsList,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: TariffDetailsProps) {
	const { t } = useTranslation('common');
	const { t: tT } = useTranslation('tariffs');
	const { getTariffTypeTitle, isTieredTariff, isFlatRateTariff } =
		useTariffTypeLookup();
	// Track if references tab has been opened (for lazy loading)
	// Initialize based on whether references tab is already active (externally controlled)
	const [hasOpenedReferences, setHasOpenedReferences] = useState(
		activeTab === 'references'
	);

	// Update hasOpenedReferences if activeTab changes to 'references' externally
	useEffect(() => {
		if (activeTab === 'references' && !hasOpenedReferences) {
			setHasOpenedReferences(true);
		}
	}, [activeTab, hasOpenedReferences]);

	const handleTabChange = (value: string) => {
		if (value === 'references' && !hasOpenedReferences) {
			setHasOpenedReferences(true);
		}
		onTabChange?.(value);
	};

	const { data, isLoading, isError } = useOne<TariffDetailsData>({
		resource: 'tariffs',
		id: tariffId,
		meta: {
			gqlQuery: TARIFF_QUERY,
			dataPath: 'tariff'
		}
	});

	// For details pages, show loading immediately to prevent flash during navigation
	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 100, // Shorter delay for details pages
		showInitialLoading: true // Show loading immediately during navigation
	});

	const tariff = data?.data;

	// Show skeleton during loading
	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="w-48 h-6 rounded bg-muted animate-pulse" />
				<div className="w-32 h-6 rounded bg-muted animate-pulse" />
				<div className="space-y-2">
					<div className="w-full h-4 rounded bg-muted animate-pulse" />
					<div className="w-3/4 h-4 rounded bg-muted animate-pulse" />
				</div>
			</div>
		);
	}

	// Only show error when not loading
	if (!isLoading && (isError || !tariff)) {
		return (
			<div className="py-8 text-center text-muted-foreground">
				{tT('details.loadError')}
			</div>
		);
	}

	// If we don't have data yet, don't render anything (let loading states handle it)
	if (!tariff) {
		return null;
	}

	const isTiered = isTieredTariff(tariff.tariffTypeEnum);
	const sortedSteps: TariffStep[] = [...(tariff.tariffSteps ?? [])].sort(
		(a, b) => a.stepSequence - b.stepSequence
	);

	const tariffTypeName = getTariffTypeTitle(tariff.tariffTypeEnum);

	return (
		<div className="flex flex-col h-full">
			{/* Header - shown when in modal mode */}
			{showHeader && (
				<EntityDetailsHeader
					icon={<TariffEntityIcon />}
					name={tariff.name}
					entityTypeName={tariffTypeName}
					entityId={tariffId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4 border-b border-gray-200 dark:border-slate-700"
				/>
			)}

		<DelayedSkeleton
			isLoading={isLoading}
			delay={300}
			showInitialLoading={false}
		>
			<Tabs
				defaultValue={activeTab !== undefined ? undefined : 'details'}
				value={activeTab}
				onValueChange={handleTabChange}
				className="w-full flex-1"
			>
				{!hideTabsList && (
					<TabsList className="gap-2 bg-transparent p-0">
						<TabsTrigger
							value="details"
							className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
						>
							<FileText className="w-4 h-4" />
							<span>{t('tabs.details')}</span>
						</TabsTrigger>
						<TabsTrigger
							value="references"
							className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
						>
							<Link2 className="w-4 h-4" />
							<span>{t('tabs.references')}</span>
						</TabsTrigger>
						<TabsTrigger
							value="audit"
							align="right"
							className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
						>
							<History className="w-4 h-4" />
							<span>{t('tabs.audit')}</span>
						</TabsTrigger>
					</TabsList>
				)}

				<TabsContent value="details" className="space-y-6 mt-6">
					<DetailSection>
						<DetailFieldsGrid>
							<DetailField
								label={tT('details.tariffType')}
								value={getTariffTypeTitle(
									tariff.tariffTypeEnum
								)}
							/>
						</DetailFieldsGrid>
					</DetailSection>
					{isTiered && sortedSteps.length > 0 ? (
						<DetailSection title={t('sections.tariff_steps')}>
							{sortedSteps.map(step => (
								<div key={step.tariffStepId} className="text-sm font-semibold text-gray-900 dark:text-white">
									<span className="text-muted-foreground">#{step.stepSequence}</span>{' '}
									{tT('details.chargeLabel')}{' '}
									<span className="text-primary">
										€{Number(step.intervalCharge ?? 0).toFixed(2)}
									</span>{' '}
									{tT('details.perLabel')}{' '}
									<PeriodDisplay value={step.intervalMinutes ?? 0} />
									{step.maxIntervals != null ? (
										<>
											{' '}{tT('details.forMaximumLabel')}{' '}
											{step.maxIntervals}{' '}
											{tT('details.timesLabel')}
										</>
									) : (
										<span className="text-muted-foreground">
											{' '}({tT('details.unlimited')})
										</span>
									)}
								</div>
							))}
						</DetailSection>
					) : isFlatRateTariff(tariff.tariffTypeEnum) ? (
						<DetailSection title={t('sections.flat_rate')}>
							<DetailFieldsGrid>
								<DetailField
									label={tT('details.flatRateAmount')}
									value={
										tariff.flatRateAmount != null
											? `${Number(tariff.flatRateAmount).toFixed(2)}`
											: t('labels.not_set')
									}
								/>
							</DetailFieldsGrid>
						</DetailSection>
					) : (
						<DetailSection title={t('sections.configuration')}>
							<DetailFieldsGrid>
								<p className="text-base font-medium text-muted-foreground">
									{tT('details.noConfigurationAvailable')}
								</p>
							</DetailFieldsGrid>
						</DetailSection>
					)}
				</TabsContent>

				<TabsContent value="audit" className="mt-6">
					<AuditTab data={{}} />
				</TabsContent>

				<TabsContent value="references" className="mt-6">
					{/* Only render references tab content after it has been opened (lazy loading) */}
					{hasOpenedReferences && (
						<TariffReferencesTab tariffId={tariffId} />
					)}
				</TabsContent>
			</Tabs>
		</DelayedSkeleton>
		</div>
	);
}
